import { ref } from 'vue';
import { defineStore } from 'pinia';
import { InitNetworkLog, DoneNetworkLog, PendingNetworkLog } from './model';
import { NetworkLogStatus } from './types';
import type {
  JSONStringValue,
  NetworkRequestData,
  NetworkResponseData,
} from './types';

const checkPendingNetworkLog = (log: PendingNetworkLog | DoneNetworkLog) => {
  if (log.status === NetworkLogStatus.DONE) {
    throw new Error('ERR_NETWORK_LOG_ALREADY_COMPLETED');
  }

  return log;
};

const useNetworkLogs = defineStore('network-logs', () => {
  const logs = ref<Record<string, PendingNetworkLog | DoneNetworkLog>>({});

  const init = () => new InitNetworkLog();

  const setPending = (
    initLog: InitNetworkLog,
    requestData: NetworkRequestData,
  ) => {
    logs.value[initLog.id] = new PendingNetworkLog(initLog, requestData);
  };

  /**
   * On pending init we may not have body request data, so we need to add in later
   */
  const appendPendingData = (
    initLog: InitNetworkLog,
    requestBody: JSONStringValue,
  ) => {
    const log = logs.value[initLog.id];
    const pendingLog = checkPendingNetworkLog(log);
    logs.value[initLog.id] = new PendingNetworkLog(initLog, {
      ...pendingLog.request,
      body: requestBody,
    });
  };

  const complete = (
    initLog: InitNetworkLog,
    responseData: NetworkResponseData,
  ) => {
    const log = logs.value[initLog.id];
    const pendingLog = checkPendingNetworkLog(log);
    logs.value[initLog.id] = new DoneNetworkLog(pendingLog, responseData);
  };

  const clear = () => (logs.value = {});

  return {
    logs,
    init,
    setPending,
    appendPendingData,
    complete,
    clear,
  };
});

export type NetworkLog = PendingNetworkLog | DoneNetworkLog;
export const networkLog = { model: { useNetworkLogs } };
